import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import CombinedInventoryNotices from '@/components/account/groups/CombinedInventoryNotices'
import RoomMappingRoomDialog from '@/components/account/groups/RoomMappingRoomDialog'
import EveryRoomOffsetDialog from '@/components/account/groups/EveryRoomOffsetDialog'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import { parseMappingErrors, type MappingErrorItem } from '@/lib/combined-inventory'
import { currencySymbol } from '@/lib/calendar-utils'
import { cn } from '@/lib/utils'
import {
  isRowChanged,
  mappingPayload,
  offsetLabel,
  rowChanges,
  sellsAsLabel,
  stagedPlansStatus,
  storedDraft,
  type MappingRoom,
  type OffsetDraft,
  type RoomDraft,
  type RoomMappingPageProps,
  type StatusTone,
} from '@/lib/room-mapping'
import { AlertCircle, BedDouble, Building2, ChevronRight, Layers, Link2, Lock, Percent, X } from 'lucide-react'

// BUSINESS SETTINGS → COMBINED INVENTORY → A FOLLOWING LISTING'S ROOM MAPPING
// (docs/plans/2026-10-16-combined-inventory.md §3.20, owner rulings 2026-10-26).
//
// ONE QUESTION: how does this listing sell the building's rooms? Each room is a Same room (of which lead
// room, at which offset from it) or an Added room, and each of a Same room's rate plans copies a plan of its
// twin, follows the room's lead plan by an offset, or is not priced. All of it is set here, in one place:
// Rooms Setup answers what each room of the building is worth and says nothing about listings.
//
// Built like Rooms Setup: a table of rooms, a row opens its editor, Apply stages (amber), and nothing is
// saved until Review & Save, whose review the server writes from the save's own rehearsal (change safety
// R1.5). Every sentence and every choice is the server's (ResolveRoomMappingPageMutation); this page holds
// the staged choices and writes a staged cell the way the server writes a saved one.
export default function RoomMapping({
  currentUser,
  listing,
  lead,
  currency,
  access,
  leadRooms,
  addedOptionLabel,
  rooms,
  notices,
  savePath,
  reviewPath,
  combinedInventoryHref,
  listings,
}: RoomMappingPageProps) {
  const sym = currencySymbol(currency)
  const offsetsOnly = access === 'offsets'

  const buildDrafts = () => Object.fromEntries(rooms.map((room) => [room.id, storedDraft(room)])) as Record<number, RoomDraft>
  const [drafts, setDrafts] = useState<Record<number, RoomDraft>>(buildDrafts)
  // Re-sync from the server after every save (the fingerprint of what it sent changes).
  const fingerprint = useMemo(() => JSON.stringify(rooms.map((room) => [room.id, storedDraft(room)])), [rooms])
  useEffect(() => {
    setDrafts(buildDrafts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint])

  const [editingRoomId, setEditingRoomId] = useState<number | null>(null)
  const [everyOpen, setEveryOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [saveErrors, setSaveErrors] = useState<MappingErrorItem[] | null>(null)
  const review = usePricingReview()

  const changedIds = rooms.filter((room) => isRowChanged(room, drafts[room.id] ?? storedDraft(room))).map((room) => room.id)
  const isDirty = changedIds.length > 0
  const sameRoomCount = rooms.filter((room) => drafts[room.id]?.kind === 'same').length

  // Leaving with staged changes asks first, on a tab close and on a link inside the app.
  useEffect(() => {
    if (!isDirty || saving) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty, saving])

  useEffect(() => {
    return router.on('before', (event) => {
      if (!isDirty || savingRef.current) return
      if (!window.confirm('You have unsaved changes. Leave anyway?')) event.preventDefault()
    })
  }, [isDirty])

  function applyRoom(roomId: number, draft: RoomDraft) {
    setDrafts((prev) => ({ ...prev, [roomId]: draft }))
  }

  function applyEveryRoom(offset: OffsetDraft) {
    setDrafts((prev) => {
      const next = { ...prev }
      for (const room of rooms) {
        const draft = next[room.id]
        if (draft?.kind === 'same') next[room.id] = { ...draft, offset: { ...offset } }
      }
      return next
    })
  }

  // "Review & Save": the server rehearses exactly this save. A refused review's sentences name their room,
  // so they land on the rows as well as in the dialog.
  function openReview() {
    const payload = mappingPayload(rooms, drafts)
    review.open(reviewPath, payload, () => save(payload), { onRefused: (errors) => setSaveErrors(errors) })
  }

  function save(payload: ReturnType<typeof mappingPayload>) {
    savingRef.current = true
    setSaving(true)
    return inertiaSave('patch', savePath, payload, {
      preserveScroll: true,
      onStart: () => setSaveErrors(null),
      onError: (errors) =>
        setSaveErrors(
          parseMappingErrors((errors as Record<string, unknown>).roomMapping) ?? [
            { roomTypeId: null, message: 'The room mapping could not be saved. Try again.' },
          ],
        ),
      onFinish: () => {
        savingRef.current = false
        setSaving(false)
      },
    })
  }

  const editingRoom = rooms.find((room) => room.id === editingRoomId) ?? null
  const roomErrors = new Map<number, string[]>()
  for (const item of saveErrors ?? []) {
    if (item.roomTypeId == null) continue
    roomErrors.set(item.roomTypeId, [...(roomErrors.get(item.roomTypeId) ?? []), item.message])
  }
  const generalErrors = (saveErrors ?? []).filter((item) => item.roomTypeId == null)

  return (
    <PortfolioLayout title={`Room mapping: ${listing.name}`} currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        <section className="animate-slide-in space-y-3" style={{ animationDelay: '0ms' }}>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {combinedInventoryHref ? (
              <Link href={combinedInventoryHref} className="inline-flex items-center gap-1 font-medium hover:text-foreground">
                <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                Combined inventory
              </Link>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" aria-hidden="true" />
                Combined inventory
              </span>
            )}
            <ChevronRight className="h-3 w-3 text-muted-foreground/50" aria-hidden="true" />
            <span>{lead.name}</span>
            <ChevronRight className="h-3 w-3 text-muted-foreground/50" aria-hidden="true" />
            <span aria-current="page" className="font-medium text-foreground">
              Room mapping: {listing.name}
            </span>
          </nav>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Room mapping: {listing.name}</h1>
              <HelpTooltip
                content={`Each room at ${listing.name} is a Same room, sold at the price of the same room at ${lead.name} plus any offset, or an Added room priced on its own. Each rate plan of a Same room copies a rate plan of that room, or follows its own lead rate plan by an offset.`}
                learnMore="/guides/base-rates"
              />
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              How {listing.name}, a following listing, sells the rooms of {lead.name}, the lead listing.
            </p>
          </div>
        </section>

        {listings.length > 1 && (
          <nav
            aria-label="Following listings"
            className="animate-slide-in flex flex-wrap items-center gap-1.5"
            style={{ animationDelay: '50ms' }}
          >
            {listings.map((item) => (
              <Link
                key={item.id}
                href={item.href}
                aria-current={item.current ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                  item.current
                    ? 'border-primary/30 bg-primary/5 text-primary'
                    : 'border-border/60 bg-white text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                )}
              >
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                {item.name}
              </Link>
            ))}
          </nav>
        )}

        {offsetsOnly && (
          <p className="animate-slide-in flex items-start gap-2 rounded-xl border border-border/60 bg-white px-4 py-3 text-xs leading-relaxed text-muted-foreground shadow-sm">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>You can change the offsets. An owner or admin changes what each room is and which rate plans it copies.</span>
          </p>
        )}

        <CombinedInventoryNotices notices={notices} className="animate-slide-in" style={{ animationDelay: '75ms' }} />

        {rooms.length > 0 ? (
          <section className="animate-slide-in space-y-3" style={{ animationDelay: '100ms' }}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-1">
              <p className="text-xs text-muted-foreground">
                {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'} at {listing.name}. Select a room to change it.
              </p>
              {sameRoomCount > 0 && (
                <Button variant="outline" size="sm" className="h-9 rounded-lg text-xs" onClick={() => setEveryOpen(true)}>
                  <Percent className="mr-1.5 h-3.5 w-3.5" />
                  Same offset for every Same room
                </Button>
              )}
            </div>

            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Room</TableHead>
                    <TableHead className="hidden w-[190px] sm:table-cell">Sells as</TableHead>
                    <TableHead className="hidden w-[104px] text-right sm:table-cell">Offset</TableHead>
                    <TableHead className="w-[150px] text-right">Rate plans</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rooms.map((room) => (
                    <RoomRow
                      key={room.id}
                      room={room}
                      draft={drafts[room.id] ?? storedDraft(room)}
                      leadRooms={leadRooms}
                      sym={sym}
                      errors={roomErrors.get(room.id) ?? []}
                      onOpen={() => setEditingRoomId(room.id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        ) : (
          <div className="animate-slide-in rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm" style={{ animationDelay: '100ms' }}>
            <BedDouble className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium text-foreground">No rooms to map yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {listing.name} has no rooms in your PMS. Its rooms appear here after its next sync.
            </p>
          </div>
        )}

        {generalErrors.length > 0 && (
          <div role="alert" className="space-y-1 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {generalErrors.map((item) => (
              <p key={item.message} className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{item.message}</span>
              </p>
            ))}
            <button
              type="button"
              className="mt-1 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-rose-700 hover:underline"
              onClick={() => setSaveErrors(null)}
            >
              <X className="h-3 w-3" aria-hidden="true" />
              Dismiss
            </button>
          </div>
        )}

        {/* The save bar, Rooms Setup's, kept in reach while a long table scrolls: it lifts into a card
            once something is staged. */}
        <div
          className={cn(
            'sticky bottom-4 z-20 flex items-center justify-end gap-3 rounded-xl px-4 py-3 transition-colors',
            isDirty && 'border border-amber-200/80 bg-white/95 shadow-sm backdrop-blur',
          )}
        >
          {isDirty && !saving && (
            <span className="text-xs font-medium text-amber-600" aria-live="polite">
              You have unsaved changes
            </span>
          )}
          <Button type="button" disabled={!isDirty || saving} className="rounded-lg px-6" onClick={openReview}>
            {saving ? 'Saving...' : 'Review & Save'}
          </Button>
        </div>
      </div>

      {editingRoom && (
        <RoomMappingRoomDialog
          key={editingRoom.id}
          open={editingRoomId != null}
          onOpenChange={(open) => !open && setEditingRoomId(null)}
          room={editingRoom}
          rooms={rooms}
          drafts={drafts}
          leadRooms={leadRooms}
          leadName={lead.name}
          addedOptionLabel={addedOptionLabel}
          access={access}
          currencySymbol={sym}
          draft={drafts[editingRoom.id] ?? storedDraft(editingRoom)}
          onApply={(draft) => applyRoom(editingRoom.id, draft)}
        />
      )}

      {everyOpen && (
        <EveryRoomOffsetDialog
          open={everyOpen}
          onOpenChange={setEveryOpen}
          listingName={listing.name}
          leadName={lead.name}
          sameRoomCount={sameRoomCount}
          currencySymbol={sym}
          onApply={applyEveryRoom}
        />
      )}

      {review.dialog}
    </PortfolioLayout>
  )
}

const TONE: Record<StatusTone, string> = {
  ok: 'text-muted-foreground',
  warn: 'font-medium text-amber-700',
  muted: 'text-muted-foreground/70',
}

// ── One room ─────────────────────────────────────────────────────────────────────────────────────
// A saved cell reads the server's words; a staged cell is written the same way and marked amber, like
// Rooms Setup's unsaved marks, until Review & Save.
function RoomRow({
  room,
  draft,
  leadRooms,
  sym,
  errors,
  onOpen,
}: {
  room: MappingRoom
  draft: RoomDraft
  leadRooms: RoomMappingPageProps['leadRooms']
  sym: string
  errors: string[]
  onOpen: () => void
}) {
  const changes = rowChanges(room, draft)
  const staged = changes.kind || changes.offset || changes.plans
  const sellsAs = changes.kind ? sellsAsLabel(draft, leadRooms) : room.sellsAs
  const offset =
    draft.kind === 'added' ? null : changes.offset || changes.kind ? offsetLabel(draft.offset, sym) ?? 'No offset' : room.offset?.label ?? 'No offset'
  const status = changes.kind || changes.plans ? stagedPlansStatus(room, draft, leadRooms) : room.plansStatus

  return (
    <>
      <TableRow
        className={cn('cursor-pointer transition-colors hover:bg-muted/40', staged && 'bg-amber-50/40 hover:bg-amber-50/70')}
        onClick={onOpen}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/8">
              {draft.kind === 'same' ? (
                <Link2 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              ) : (
                <BedDouble className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0">
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  className="truncate text-left text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpen()
                  }}
                >
                  {room.name}
                </button>
                {staged && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                    Unsaved
                  </span>
                )}
              </span>
              <p className="text-xs tabular-nums text-muted-foreground">{room.meta}</p>
              {/* A phone has no room for the two middle columns, so they read under the name. */}
              <p className={cn('text-xs sm:hidden', staged ? 'font-medium text-amber-700' : 'text-foreground')}>
                {offset ? `${sellsAs} · ${offset}` : sellsAs}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell className={cn('hidden text-sm sm:table-cell', changes.kind ? 'font-medium text-amber-700' : 'text-foreground')}>
          {sellsAs}
        </TableCell>
        <TableCell
          className={cn('hidden text-right text-sm tabular-nums sm:table-cell', changes.offset ? 'font-semibold text-amber-700' : 'text-foreground')}
        >
          {offset}
        </TableCell>
        <TableCell className={cn('text-right text-xs tabular-nums', changes.plans || changes.kind ? 'font-medium text-amber-700' : TONE[status.tone])}>
          {status.label}
        </TableCell>
        <TableCell className="w-8 text-right">
          <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground/50" />
        </TableCell>
      </TableRow>
      {errors.length > 0 && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="pt-0">
            {errors.map((message) => (
              <p key={message} role="alert" className="flex items-start gap-1.5 text-xs text-rose-700">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{message}</span>
              </p>
            ))}
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
